/**
 * KioskContext — manages SMS kiosk mode state (ADR-073 Decision 5).
 *
 * Kiosk mode locks the SMS app into a visitor-facing check-in UI.
 * State persists in localStorage so the kiosk survives refreshes/restarts.
 * Exiting requires an auth challenge (staff credentials).
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getFromLocalStorage,
  saveToLocalStorage,
  removeFromLocalStorage,
} from '../lib/storage';

// =============================================================================
// Types
// =============================================================================

interface KioskState {
  active: boolean;
  buildingId: number | null;
  buildingName: string;
  schoolId: number | null;
  schoolName: string;
}

interface KioskContextType {
  /** Whether kiosk mode is currently active */
  isKioskMode: boolean;
  /** The building the kiosk is bound to */
  buildingId: number | null;
  buildingName: string;
  /** The school the kiosk is bound to */
  schoolId: number | null;
  schoolName: string;
  /** Enter kiosk mode — binds to a specific building */
  enterKioskMode: (params: {
    buildingId: number;
    buildingName: string;
    schoolId: number;
    schoolName: string;
  }) => void;
  /** Exit kiosk mode — call only after successful auth challenge */
  exitKioskMode: () => void;
}

// =============================================================================
// Constants
// =============================================================================

const KIOSK_STORAGE_KEY = 'aegix_sms_kiosk';

const DEFAULT_STATE: KioskState = {
  active: false,
  buildingId: null,
  buildingName: '',
  schoolId: null,
  schoolName: '',
};

// =============================================================================
// Context
// =============================================================================

const KioskContext = createContext<KioskContextType | undefined>(undefined);

export function KioskProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<KioskState>(DEFAULT_STATE);

  // Load persisted kiosk state on mount
  useEffect(() => {
    const stored = getFromLocalStorage<KioskState>(KIOSK_STORAGE_KEY);
    if (stored && stored.active) {
      setState(stored);
    }
  }, []);

  const enterKioskMode = useCallback(
    (params: {
      buildingId: number;
      buildingName: string;
      schoolId: number;
      schoolName: string;
    }) => {
      const newState: KioskState = {
        active: true,
        buildingId: params.buildingId,
        buildingName: params.buildingName,
        schoolId: params.schoolId,
        schoolName: params.schoolName,
      };
      setState(newState);
      saveToLocalStorage(KIOSK_STORAGE_KEY, newState);
    },
    [],
  );

  const exitKioskMode = useCallback(() => {
    setState(DEFAULT_STATE);
    removeFromLocalStorage(KIOSK_STORAGE_KEY);
  }, []);

  const value: KioskContextType = {
    isKioskMode: state.active,
    buildingId: state.buildingId,
    buildingName: state.buildingName,
    schoolId: state.schoolId,
    schoolName: state.schoolName,
    enterKioskMode,
    exitKioskMode,
  };

  return (
    <KioskContext.Provider value={value}>{children}</KioskContext.Provider>
  );
}

export function useKiosk() {
  const context = useContext(KioskContext);
  if (context === undefined) {
    throw new Error('useKiosk must be used within a KioskProvider');
  }
  return context;
}
